import { Switch, Route, Redirect, useLocation } from "wouter";
import { useEffect, useState, lazy, Suspense } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";

import { Navbar } from "./components/layout/Navbar";
import { ErrorBoundary } from "./components/shared/ErrorBoundary";
import { NavigationProgress } from "./components/shared/NavigationProgress";

import { TranslationProvider } from "./lib/TranslationProvider";
import { SLUG_TRANS } from "@shared/route-slugs";
import { NON_EN_LANG_CODES } from "@shared/languages";

const Footer = lazy(() => import("./components/layout/Footer").then(m => ({ default: m.Footer })));
const ScrollToTopButton = lazy(() => import("./components/shared/ScrollToTopButton").then(m => ({ default: m.ScrollToTopButton })));
const AiAssistant = lazy(() => import("./components/shared/AiAssistant").then(m => ({ default: m.AiAssistant })));

const CompareBanner = lazy(() => import("./components/shared/CompareBanner").then(m => ({ default: m.CompareBanner })));
const ItineraryBanner = lazy(() => import("./components/shared/ItineraryBanner").then(m => ({ default: m.ItineraryBanner })));
const CookieBanner = lazy(() => import("./components/shared/CookieBanner").then(m => ({ default: m.CookieBanner })));
const NewsletterPopup = lazy(() => import("./components/shared/NewsletterPopup").then(m => ({ default: m.NewsletterPopup })));
const InstallPrompt = lazy(() => import("./components/shared/InstallPrompt").then(m => ({ default: m.InstallPrompt })));

import { useAppStore } from "./store/use-app-store";
import { getPathLanguage } from "./lib/locale";

const Home = lazy(() => import("./pages/Home"));
const Region = lazy(() => import("./pages/Region"));
const Location = lazy(() => import("./pages/Location"));
const Compare = lazy(() => import("./pages/Compare"));
const NationalParks = lazy(() => import("./pages/NationalParks"));
const NationalParkDetail = lazy(() => import("./pages/NationalParkDetail"));
const BestTimeToVisit = lazy(() => import("./pages/BestTimeToVisit"));
const BudgetCalculator = lazy(() => import("./pages/BudgetCalculator"));
const Favorites = lazy(() => import("./pages/Favorites"));
const TravelGuides = lazy(() => import("./pages/TravelGuides"));
const TravelGuide = lazy(() => import("./pages/TravelGuide"));
const Camping = lazy(() => import("./pages/Camping"));
const Sailing = lazy(() => import("./pages/Sailing"));
const About = lazy(() => import("./pages/About"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Impressum = lazy(() => import("./pages/Impressum"));
const Admin = lazy(() => import("./pages/Admin"));
const SeoAudit = lazy(() => import("./pages/SeoAudit"));
const Festivals = lazy(() => import("./pages/Festivals"));
const FestivalDetail = lazy(() => import("./pages/FestivalDetail"));
const Airports = lazy(() => import("./pages/Airports"));
const DestinationSeo = lazy(() => import("./pages/DestinationSeo"));
const DestinationBlog = lazy(() => import("./pages/DestinationBlog"));
const ItineraryPage = lazy(() => import("./pages/Itinerary"));
const ItineraryGuides = lazy(() => import("./pages/ItineraryGuides"));
const ItineraryBlog = lazy(() => import("./pages/ItineraryBlog"));
const TripReport = lazy(() => import("./pages/TripReport"));
const Islands = lazy(() => import("./pages/Islands"));
const IslandGuide = lazy(() => import("./pages/IslandGuide"));
const Locations = lazy(() => import("./pages/Locations"));
const Regions = lazy(() => import("./pages/Regions"));
const DayTrips = lazy(() => import("./pages/DayTrips"));
const FerryRoutes = lazy(() => import("./pages/FerryRoutes"));
const PackingList = lazy(() => import("./pages/PackingList"));
const TripReports = lazy(() => import("./pages/TripReports"));
const Webcams = lazy(() => import("./pages/Webcams"));
const MapExplorer = lazy(() => import("./pages/MapExplorer"));
const Hiking = lazy(() => import("./pages/Hiking"));
const TripPlan = lazy(() => import("./pages/TripPlan"));
const SmartScore = lazy(() => import("./pages/SmartScore"));
const SeaTemperature = lazy(() => import("./pages/SeaTemperature"));
const Beaches = lazy(() => import("./pages/Beaches"));
const BeachDetail = lazy(() => import("./pages/BeachDetail"));
const MarinaDetail = lazy(() => import("./pages/MarinaDetail"));
const MediaKit = lazy(() => import("./pages/MediaKit"));
const NotFound = lazy(() => import("./pages/not-found"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const BlogIndex = lazy(() => import("./pages/BlogIndex"));
const CmsDashboard = lazy(() => import("./pages/cms/CmsDashboard"));
const CmsCreate = lazy(() => import("./pages/cms/CmsCreate"));
const CmsEdit = lazy(() => import("./pages/cms/CmsEdit"));
function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);
  }, [location]);
  return null;
}

function AnalyticsTracker() {
  const [location] = useLocation();
  const firstRunRef = (typeof window !== "undefined")
    ? (window as unknown as { __scAnalyticsFirstRun?: { value: boolean } }).__scAnalyticsFirstRun ??
      ((window as unknown as { __scAnalyticsFirstRun?: { value: boolean } }).__scAnalyticsFirstRun = { value: true })
    : { value: true };

  useEffect(() => {
    // Server middleware already counted the initial HTML request — skip the
    // first SPA route render to avoid double-counting (server dedup window
    // would catch it anyway, but skipping is cleaner).
    if (firstRunRef.value) {
      firstRunRef.value = false;
      // Report PWA launch once if running in standalone mode.
      import("./lib/analytics").then(({ reportPwaLaunchOnce }) => reportPwaLaunchOnce());
      return;
    }
    import("./lib/analytics").then(({ trackEvent }) => trackEvent("page_view"));
  }, [location]);

  return null;
}

function LanguageFromUrl() {
  const [location] = useLocation();
  const { language, setLanguage } = useAppStore();
  const [hydrated, setHydrated] = useState(() => useAppStore.persist.hasHydrated());

  useEffect(() => {
    if (hydrated) return;
    return useAppStore.persist.onFinishHydration(() => setHydrated(true));
  }, [hydrated]);

  // URL is the source of truth. Reconcile after hydration AND on every URL change,
  // so a stale persisted language (e.g. "hr" from a previous visit) cannot overwrite
  // the language implied by the current /de/, /fr/, /it/, /nl/, /hr/, /pl/ prefix.
  useEffect(() => {
    const urlLang = getPathLanguage(location);
    if (language !== urlLang) setLanguage(urlLang);
  }, [location, hydrated, language, setLanguage]);

  return null;
}

function ThemeApplier() {
  const { theme } = useAppStore();
  const [hydrated, setHydrated] = useState(() => useAppStore.persist.hasHydrated());

  useEffect(() => {
    if (hydrated) return;
    return useAppStore.persist.onFinishHydration(() => setHydrated(true));
  }, [hydrated]);

  useEffect(() => {
    if (!hydrated) return;
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme, hydrated]);
  return null;
}

function LangApplier() {
  const { language } = useAppStore();
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);
  return null;
}

// Verify the locally cached user (kept in localStorage by the Zustand store
// for instant render) still matches a live server-side session. Runs once on
// app boot, then again whenever the tab regains focus, so a server restart,
// cookie expiry, or sign-out from another tab is reflected promptly.
//
// On a real 401, clear the cached user. On a network failure, do nothing —
// we don't want to log users out on every transient blip.
//
// Reads the latest user via useAppStore.getState() inside verify() (instead
// of closing over the mount-time `user` value) so a sign-in that happens
// AFTER mount is still reconcilable on the next focus event.
function AuthSessionSync() {
  useEffect(() => {
    let cancelled = false;
    let lastVerifiedAt = 0;
    const verify = async () => {
      // Anonymous visitors have no server session to reconcile. Avoid a 401
      // request on every public page load and focus event.
      if (!useAppStore.getState().user) return;
      const now = Date.now();
      if (now - lastVerifiedAt < 15 * 60 * 1000) return;
      lastVerifiedAt = now;
      try {
        const res = await fetch("/api/auth/me", { credentials: "include" });
        if (cancelled) return;
        const { user, setUser, csrfToken, setCsrfToken } = useAppStore.getState();
        if (res.status === 401) {
          if (user) setUser(null);
          if (csrfToken) setCsrfToken(null);
          return;
        }
        if (!res.ok) return;
        const fresh = await res.json();
        if (cancelled) return;
        if (
          !user ||
          user.id !== fresh.id ||
          user.email !== fresh.email ||
          user.role !== fresh.role
        ) {
          setUser({ id: fresh.id, email: fresh.email, role: fresh.role });
        }
        if (fresh.csrfToken && fresh.csrfToken !== csrfToken) {
          setCsrfToken(fresh.csrfToken);
        }
      } catch {
        /* network blip — leave cached user + token untouched */
      }
    };
    verify();
    const onFocus = () => verify();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
    };
  }, []);
  return null;
}

function DeferredAiAssistant() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let idleId: number | undefined;
    const activate = () => setReady(true);

    window.addEventListener("pointerdown", activate, { once: true, passive: true });
    window.addEventListener("keydown", activate, { once: true });
    if ("requestIdleCallback" in window) {
      idleId = (window as Window & {
        requestIdleCallback: (callback: () => void, options?: { timeout: number }) => number;
        cancelIdleCallback: (id: number) => void;
      }).requestIdleCallback(activate, { timeout: 5000 });
    } else {
      timer = setTimeout(activate, 5000);
    }

    return () => {
      window.removeEventListener("pointerdown", activate);
      window.removeEventListener("keydown", activate);
      if (timer) clearTimeout(timer);
      if (idleId !== undefined && "cancelIdleCallback" in window) {
        (window as Window & { cancelIdleCallback: (id: number) => void }).cancelIdleCallback(idleId);
      }
    };
  }, []);

  if (!ready) return null;
  return <Suspense fallback={null}><AiAssistant /></Suspense>;
}

function PageSkeleton() {
  return (
    <>
      <NavigationProgress />
      <div className="min-h-screen" />
    </>
  );
}

// Legacy top-level slugs that redirect to /guides/:slug (EN) or the translated
// equivalent in each non-EN language. All source slugs are in SLUG_TRANS so
// translations are derived automatically — no manual per-language lists needed.
const LEGACY_GUIDE_SLUGS = [
  'easter-holidays-croatia', 'split-hotels-parking', 'dubrovnik-hotels-parking',
  'zadar-travel-guide', 'is-cavtat-expensive', 'where-not-to-go-in-croatia',
  'vinistra-wine-festival-guide', 'makarska-tourism-boom-2026',
  'croatia-border-delays-ees-2026', 'is-dubrovnik-expensive',
  'is-split-expensive', 'is-hvar-expensive', 'best-beaches-croatia',
] as const;

function Router() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageSkeleton />}>
        <Switch>
          {/* English routes */}
          <Route path="/" component={Home} />
          <Route path="/islands" component={Islands} />
          <Route path="/islands/:slug" component={IslandGuide} />
          <Route path="/regions" component={Regions} />
          <Route path="/locations" component={Locations} />
          <Route path="/national-parks" component={NationalParks} />
          <Route path="/guides" component={TravelGuides} />
          <Route path="/camping" component={Camping} />
          <Route path="/sailing" component={Sailing} />
          <Route path="/best-time" component={BestTimeToVisit} />
          <Route path="/budget-calculator" component={BudgetCalculator} />
          <Route path="/compare" component={Compare} />
          <Route path="/itinerary" component={ItineraryPage} />
          <Route path="/trip-report" component={TripReport} />
          <Route path="/favorites" component={Favorites} />
          <Route path="/about" component={About} />
          <Route path="/media-kit" component={MediaKit} />
          <Route path="/privacy" component={PrivacyPolicy} />
          <Route path="/terms" component={TermsOfService} />
          <Route path="/cookies" component={CookiePolicy} />
          <Route path="/impressum" component={Impressum} />
          <Route path="/admin" component={Admin} />
          <Route path="/seo-audit" component={SeoAudit} />
          <Route path="/festivals" component={Festivals} />
          <Route path="/festivals/:slug" component={FestivalDetail} />
          <Route path="/airports" component={Airports} />
          <Route path="/itinerary-guides" component={ItineraryGuides} />
          <Route path="/itinerary-guide/:slug" component={ItineraryBlog} />
          <Route path="/guides/:slug" component={TravelGuide} />
          <Route path="/region/:slug" component={Region} />
          <Route path="/location/:slug" component={Location} />
          <Route path="/national-park/:slug" component={NationalParkDetail} />
          <Route path="/travel-cost-guide/:slug" component={DestinationSeo} />
          <Route path="/destinations/:slug">{(params: { slug: string }) => <Redirect to={`/travel-cost-guide/${params.slug}`} />}</Route>
          <Route path="/location-guide/:slug" component={DestinationBlog} />
          <Route path="/day-trips" component={DayTrips} />
          <Route path="/ferry-routes" component={FerryRoutes} />
          <Route path="/packing-list" component={PackingList} />
          <Route path="/trip-reports" component={TripReports} />
          <Route path="/webcams" component={Webcams} />
          <Route path="/map" component={MapExplorer} />
          <Route path="/hiking" component={Hiking} />
          {LEGACY_GUIDE_SLUGS.map((slug) => (
            <Route key={`en-lr-${slug}`} path={`/${slug}`}>
              <Redirect to={`/guides/${slug}`} />
            </Route>
          ))}
          <Route path="/smart-score" component={SmartScore} />
          <Route path="/croatia-sea-temperature" component={SeaTemperature} />
          <Route path="/sea-temperature" component={() => { window.location.replace("/croatia-sea-temperature"); return null; }} />
          <Route path="/beaches" component={Beaches} />
          <Route path="/beaches/:slug" component={BeachDetail} />
          <Route path="/marinas/:slug" component={MarinaDetail} />
          <Route path="/blog" component={BlogIndex} />
          <Route path="/blog/:slug" component={BlogPost} />
          <Route path="/ai-cms/dashboard" component={CmsDashboard} />
          <Route path="/ai-cms/create" component={CmsCreate} />
          <Route path="/ai-cms/edit/:id" component={CmsEdit} />
          {/* Non-English routes — generated from shared slug translations */}
          {NON_EN_LANG_CODES.flatMap((lang) => {
            const t = (seg: string): string =>
              (SLUG_TRANS as Record<string, Record<string, string>>)[seg]?.[lang] ?? seg;
            return [
              <Route key={`${lang}`} path={`/${lang}`} component={Home} />,
              <Route key={`${lang}-islands`} path={`/${lang}/${t('islands')}`} component={Islands} />,
              <Route key={`${lang}-island-slug`} path={`/${lang}/${t('islands')}/:slug`} component={IslandGuide} />,
              <Route key={`${lang}-regions`} path={`/${lang}/${t('regions')}`} component={Regions} />,
              <Route key={`${lang}-locations`} path={`/${lang}/${t('locations')}`} component={Locations} />,
              <Route key={`${lang}-nps`} path={`/${lang}/${t('national-parks')}`} component={NationalParks} />,
              <Route key={`${lang}-guides`} path={`/${lang}/${t('guides')}`} component={TravelGuides} />,
              <Route key={`${lang}-camping`} path={`/${lang}/${t('camping')}`} component={Camping} />,
              <Route key={`${lang}-sailing`} path={`/${lang}/${t('sailing')}`} component={Sailing} />,
              <Route key={`${lang}-best-time`} path={`/${lang}/${t('best-time')}`} component={BestTimeToVisit} />,
              <Route key={`${lang}-budget`} path={`/${lang}/${t('budget-calculator')}`} component={BudgetCalculator} />,
              <Route key={`${lang}-compare`} path={`/${lang}/${t('compare')}`} component={Compare} />,
              <Route key={`${lang}-itinerary`} path={`/${lang}/${t('itinerary')}`} component={ItineraryPage} />,
              <Route key={`${lang}-trip-report`} path={`/${lang}/${t('trip-report')}`} component={TripReport} />,
              <Route key={`${lang}-favorites`} path={`/${lang}/${t('favorites')}`} component={Favorites} />,
              <Route key={`${lang}-media-kit`} path={`/${lang}/${t('media-kit')}`} component={MediaKit} />,
              <Route key={`${lang}-about`} path={`/${lang}/${t('about')}`} component={About} />,
              <Route key={`${lang}-privacy`} path={`/${lang}/${t('privacy')}`} component={PrivacyPolicy} />,
              <Route key={`${lang}-terms`} path={`/${lang}/${t('terms')}`} component={TermsOfService} />,
              <Route key={`${lang}-cookies`} path={`/${lang}/${t('cookies')}`} component={CookiePolicy} />,
              <Route key={`${lang}-festivals`} path={`/${lang}/${t('festivals')}`} component={Festivals} />,
              <Route key={`${lang}-festivals-slug`} path={`/${lang}/${t('festivals')}/:slug`} component={FestivalDetail} />,
              <Route key={`${lang}-airports`} path={`/${lang}/${t('airports')}`} component={Airports} />,
              <Route key={`${lang}-itin-guides`} path={`/${lang}/${t('itinerary-guides')}`} component={ItineraryGuides} />,
              <Route key={`${lang}-itin-guide-slug`} path={`/${lang}/${t('itinerary-guide')}/:slug`} component={ItineraryBlog} />,
              <Route key={`${lang}-guides-slug`} path={`/${lang}/${t('guides')}/:slug`} component={TravelGuide} />,
              <Route key={`${lang}-region-slug`} path={`/${lang}/${t('region')}/:slug`} component={Region} />,
              <Route key={`${lang}-location-slug`} path={`/${lang}/${t('location')}/:slug`} component={Location} />,
              <Route key={`${lang}-park-slug`} path={`/${lang}/${t('national-park')}/:slug`} component={NationalParkDetail} />,
              <Route key={`${lang}-cost-slug`} path={`/${lang}/${t('travel-cost-guide')}/:slug`} component={DestinationSeo} />,
              <Route key={`${lang}-dest-redir`} path={`/${lang}/${t('destinations')}/:slug`}>
                {(params: { slug: string }) => <Redirect to={`/${lang}/${t('travel-cost-guide')}/${params.slug}`} />}
              </Route>,
              <Route key={`${lang}-locguide-slug`} path={`/${lang}/${t('location-guide')}/:slug`} component={DestinationBlog} />,
              <Route key={`${lang}-day-trips`} path={`/${lang}/${t('day-trips')}`} component={DayTrips} />,
              <Route key={`${lang}-ferry`} path={`/${lang}/${t('ferry-routes')}`} component={FerryRoutes} />,
              <Route key={`${lang}-packing`} path={`/${lang}/${t('packing-list')}`} component={PackingList} />,
              <Route key={`${lang}-reports`} path={`/${lang}/${t('trip-reports')}`} component={TripReports} />,
              <Route key={`${lang}-webcams`} path={`/${lang}/${t('webcams')}`} component={Webcams} />,
              <Route key={`${lang}-map`} path={`/${lang}/${t('map')}`} component={MapExplorer} />,
              <Route key={`${lang}-hiking`} path={`/${lang}/${t('hiking')}`} component={Hiking} />,
              ...LEGACY_GUIDE_SLUGS.map((slug) => (
                <Route key={`${lang}-lr-${slug}`} path={`/${lang}/${t(slug)}`}>
                  <Redirect to={`/${lang}/${t('guides')}/${slug}`} />
                </Route>
              )),
              <Route key={`${lang}-smart-score`} path={`/${lang}/smart-score`} component={SmartScore} />,
              <Route key={`${lang}-sea-temp`} path={`/${lang}/${t('croatia-sea-temperature')}`} component={SeaTemperature} />,
              <Route key={`${lang}-beaches`} path={`/${lang}/${t('beaches')}`} component={Beaches} />,
              <Route key={`${lang}-beach-slug`} path={`/${lang}/${t('beaches')}/:slug`} component={BeachDetail} />,
              <Route key={`${lang}-marina-slug`} path={`/${lang}/${t('marinas')}/:slug`} component={MarinaDetail} />,
              <Route key={`${lang}-blog`} path={`/${lang}/blog`} component={BlogIndex} />,
              <Route key={`${lang}-blog-slug`} path={`/${lang}/blog/:slug`} component={BlogPost} />,
              <Route key={`${lang}-impressum`} path={`/${lang}/impressum`} component={Impressum} />,
            ];
          })}

          <Route path="/trip-plan/:shareId" component={TripPlan} />

          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TranslationProvider>
      <TooltipProvider>
        <div className="min-h-screen flex flex-col bg-background selection:bg-primary/30">
          <ScrollToTop />
          <AnalyticsTracker />
          <LanguageFromUrl />
          <ThemeApplier />
          <LangApplier />
          <AuthSessionSync />
          <Navbar />
          <main className="flex-1">
            <Router />
          </main>
          <Suspense fallback={null}><Footer /></Suspense>
          <DeferredAiAssistant />
          <Suspense fallback={null}><ScrollToTopButton /></Suspense>
          <Suspense fallback={null}><ItineraryBanner /></Suspense>
          <Suspense fallback={null}><CompareBanner /></Suspense>
          <Suspense fallback={null}><CookieBanner /></Suspense>
          <Suspense fallback={null}><NewsletterPopup /></Suspense>
          <Suspense fallback={null}><InstallPrompt /></Suspense>
        </div>
        <Toaster />
      </TooltipProvider>
      </TranslationProvider>
    </QueryClientProvider>
  );
}

export default App;
